import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { injectQuery } from '@ngneat/query'
import { ToastService } from './toast.service'
import { LoggerService } from './logger.service'
import { User, UserWithOrganization } from './users.service'
import { Organization } from './organizations.service'
import { Call, CallProcessingStatus, CallStatus, CallType, MetricType } from './calls.service'
import { queryKeys } from './query-keys'
import { TimeInterval } from './common/time-interval/time-interval.component'
import { catchError, EMPTY, map, Observable, tap } from 'rxjs'
import { generateHeadersWithTimezone } from './utils'
import { DownloadsService } from './downloads.service'
import type { SupportUserActivityResult, UserActivityRow } from '../../../backend/src/metrics/support-user-activity-api.dto'

export type AdminMetricQueryResult = {
  list: MetricDataPoint[]
  total: number
}

export type AdminCompletedCallsQueryResult = {
  list: CompletedCallDetails[]
  total: number
}

export enum MetricComponent {
  USER = 'USER',
  BOT = 'BOT',
  BACKEND = 'BACKEND',
  CALENDAR_SYNCHRONIZATION = 'CALENDAR_SYNCHRONIZATION',
  BOT_MANAGER = 'BOT_MANAGER',
  CALL_DELETER = 'CALL_DELETER',
}

export type MetricDataPoint = {
  id: number
  userId: number | null
  user: User | null
  callId: number | null
  call: Call | null
  organizationId: number | null
  organization: Organization | null
  component: MetricComponent
  metric: MetricType
  metadata: any
  timestamp: Date
}

export type ActivityLogSettingsChange = { field: string; value: unknown }

export type ActivityLogEntry = {
  id: number
  metric: MetricType
  timestamp: string
  user: { id: number; name: string } | null
  performedByBriefAiSupport: boolean
  organization: { id: number; name: string } | null
  call: { id: number; name: string } | null
  settingsChanges: ActivityLogSettingsChange[] | null
  affectedUserName: string | null
}

export type ActivityLogQuery = {
  organizationIds: number[]
  userId: number | null
  metrics: MetricType[]
  startDate: string | null
  endDate: string | null
  page: number
  pageSize: number
  sortField: string | null
  sortDirection: string | null
}

export type ActivityLogQueryResult = {
  list: ActivityLogEntry[]
  total: number
}

export type UsageByUserMetric = {
  user: UserWithOrganization
  usageByInterval: {
    intervalLabel: string
    intervalType: 'DAY' | 'MONTH'
    duration: number | null
    count: number | null
  }[]
  totalDuration: number | null
  totalCount: number | null
  active: boolean
}

export type RawUsageByUserMetric = {
  [userId: string]: UsageByUserMetric
}

export type PaginatedRawUsageByUserMetric = {
  data: RawUsageByUserMetric
  hasMore: boolean
}

export type UsageByOrganizationMetric = {
  organization: Organization
  activeUserCount: number
  usageByInterval: {
    intervalLabel: string
    intervalType: 'DAY' | 'MONTH'
    duration: number | null
    count: number | null
  }[]
}

export type RawUsageByOrganizationMetric = {
  [organizationId: string]: UsageByOrganizationMetric
}

export function normalizeUsageByUserMetric(data: RawUsageByUserMetric): UsageByUserMetric[] {
  return Object.values(data).map((value) => ({
    user: value.user,
    usageByInterval: Object.entries(value.usageByInterval).map(([intervalLabel, data]: [string, any]) => ({
      intervalLabel,
      ...data,
    })),
    totalDuration: value.totalDuration,
    totalCount: value.totalCount,
    active: value.active,
  }))
}

export function normalizeOrganizationUsageMetric(data: RawUsageByOrganizationMetric): UsageByOrganizationMetric[] {
  return Object.values(data).map((value) => ({
    organization: value.organization,
    activeUserCount: value.activeUserCount,
    usageByInterval: Object.entries(value.usageByInterval).map(([intervalLabel, data]: [string, any]) => ({
      intervalLabel,
      ...data,
    })),
  }))
}

export type CompletedCallUser = {
  userId: number
  firstName: string
  lastName: string
  email: string
  completedCallId: number
}

export type DeletionLifecycleStage = 'INITIAL_RETENTION' | 'SOFT_DELETED' | 'HARD_DELETED'

export type CompletedCallDetails = {
  callId: number
  name: string
  organizationId: number
  duration: number | null
  actualStartDate: Date | null
  status: CallStatus
  type: CallType
  users: CompletedCallUser[]
  latestCallSessionStatus: string | null
  processingStatus: CallProcessingStatus | null
  avgConfidenceScore: number | null
  avgRms: number | null
  avgHfRatio: number | null
  meetingRoomNames: string[]
  deletionLifecycleStage: DeletionLifecycleStage
}

export type FeatureAdoptionForUser = {
  userId: number
  firstName: string
  lastName: string
  hasUploadedAgenda: boolean
  hasLabelledSpeaker: boolean
  hasUsedEditor: boolean
  hasUsedAiActions: boolean
  hasEnabledSpeakerLabelingMode: boolean
  hasChangedMinutingStyles: boolean
  hasUsedJoinNow: boolean
  hasUsedPausing: boolean
}

export type MeetingRoomMetric = {
  id: number
  canonicalName: string
  organizationId: number
  averageConfidenceScore: number | null
  averageRms: number | null
  averageHfRatio: number | null
  callCount: number
}

export type EngagementForUser = {
  userId: number
  firstName: string
  lastName: string
  callCount: number
  callsViewed: number
  callsEdited: number
  callsSpeakerLabeled: number
  callsExported: number
  averageSecondsToEdit: number
}

export type PerformanceMetric = {
  name: string
  sampleCount: number
  avgLatencyMs: number
  minLatencyMs: number
  maxLatencyMs: number
  p50LatencyMs: number
  p95LatencyMs: number
  p99LatencyMs: number
}

export type ActivityPrecision = 'HOUR' | 'DAY' | 'WEEK'

export type ActivityDataPoint = {
  intervalLabel: string
  activeUsers: number
  callViews: number
  appOpens: number
  editorSaves: number
}

export type ActivityMetricsResult = {
  precision: ActivityPrecision
  dataPoints: ActivityDataPoint[]
}

export type { SupportUserActivityResult, UserActivityRow }

@Injectable({
  providedIn: 'root',
})
export class MetricsService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  downloadsService = inject(DownloadsService)

  constructor() {}

  sendMetric(callId: number | null, type: MetricType, metadata: object = {}) {
    const params = {
      type,
      callId,
      ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    }
    this.httpClient.post<void>(`/api/metrics`, params).subscribe({
      error: (err) => {
        this.loggerService.error(`Failed to send metric ${type} for call ${callId}.`, err)
      },
    })
  }

  getMetrics(
    userIds: number[],
    callId: number | null,
    organizationId: number | null,
    component: MetricComponent | null,
    metrics: MetricType[],
    startDate: Date | null,
    endDate: Date | null,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(userIds.length > 0 ? { userId: userIds.map(String) } : {}),
      ...(callId ? { callId: callId.toString() } : {}),
      ...(organizationId ? { organizationId: organizationId.toString() } : {}),
      ...(component ? { component } : {}),
      ...(metrics.length > 0 ? { metric: metrics } : {}),
      ...(startDate ? { startDate: startDate.toISOString() } : {}),
      ...(endDate ? { endDate: endDate.toISOString() } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.list(params),
      queryFn: () =>
        this.httpClient.get<AdminMetricQueryResult>('/api/metrics', {
          params,
        }),
    }).result$
  }

  getActivityLog(query: ActivityLogQuery) {
    const params = {
      organizationIds: query.organizationIds.map((organizationId) => organizationId.toString()),
      ...(query.userId !== null ? { userId: query.userId.toString() } : {}),
      ...(query.metrics.length > 0 ? { metrics: query.metrics } : {}),
      ...(query.startDate !== null ? { startDate: query.startDate } : {}),
      ...(query.endDate !== null ? { endDate: query.endDate } : {}),
      ...(query.sortField !== null ? { sortField: query.sortField } : {}),
      ...(query.sortDirection !== null ? { sortDirection: query.sortDirection } : {}),
      page: query.page,
      pageSize: query.pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.activityLog(
        query.organizationIds,
        query.userId,
        query.metrics,
        query.startDate,
        query.endDate,
        query.page,
        query.pageSize,
        query.sortField,
        query.sortDirection
      ),
      queryFn: () => this.httpClient.get<ActivityLogQueryResult>('/api/metrics/activity-log', { params }),
    }).result$
  }

  getUsageByUserMetrics(
    type: string,
    userId: number | null,
    organizationIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    fromDate: Date | null,
    pageSize: number | null
  ) {
    const params = {
      ...(userId ? { userId } : {}),
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(fromDate ? { fromDateString: fromDate.toISOString() } : {}),
      ...(pageSize ? { pageSize } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.usageByUser(type, params),
      queryFn: () =>
        this.httpClient
          .get<PaginatedRawUsageByUserMetric>('/api/metrics/usage-by-user', {
            params,
          })
          .pipe(
            map((response) => ({
              list: normalizeUsageByUserMetric(response.data),
              hasMore: response.hasMore,
            }))
          ),
    }).result$
  }

  getUsageByOrganizationMetrics(organizationIds: number[], fromDate: Date | null) {
    const params = {
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(fromDate ? { fromDateString: fromDate.toISOString() } : {}),
    }

    return this.query({
      queryKey: queryKeys.metrics.usageByOrganization(params),
      queryFn: () => this.httpClient.get<any>('/api/metrics/usage-by-organization', { params }).pipe(map((data) => normalizeOrganizationUsageMetric(data))),
    }).result$
  }

  getCompletedCalls(
    userId: number | null,
    organizationIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    timeInterval: TimeInterval,
    nameQuery: string | null,
    sortField: string | null,
    sortDirection: string | null,
    page: number,
    pageSize: number
  ) {
    const params = {
      ...(userId ? { userId: userId.toString() } : {}),
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(timeInterval.from ? { fromDateString: timeInterval.from.toISOString() } : {}),
      ...(timeInterval.to ? { toDateString: timeInterval.to.toISOString() } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(nameQuery ? { nameQuery } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      page,
      pageSize,
    }
    return this.query({
      queryKey: queryKeys.metrics.completedCalls(params),
      queryFn: () =>
        this.httpClient.get<AdminCompletedCallsQueryResult>('/api/metrics/completed-calls', {
          params,
        }),
    }).result$
  }

  getFeatureAdoptionByUser(organizationId: number) {
    return this.query({
      queryKey: queryKeys.metrics.featureAdoptionByUser(organizationId),
      queryFn: () =>
        this.httpClient.get<FeatureAdoptionForUser[]>('/api/metrics/feature-adoption-by-user', {
          params: {
            organizationId: organizationId,
          },
        }),
    }).result$
  }

  getEngagementByUser(organizationId: number) {
    return this.query({
      queryKey: queryKeys.metrics.engagementByUser(organizationId),
      queryFn: () =>
        this.httpClient.get<EngagementForUser[]>('/api/metrics/engagement-by-user', {
          params: {
            organizationId: organizationId,
          },
        }),
    }).result$
  }

  getPerformanceMetrics(organizationId: number | null, startDate: Date | null, endDate: Date | null) {
    const startDateString = startDate !== null ? startDate.toISOString() : null
    const endDateString = endDate !== null ? endDate.toISOString() : null
    const params = {
      ...(organizationId !== null ? { organizationId: organizationId.toString() } : {}),
      ...(startDateString !== null ? { startDateString } : {}),
      ...(endDateString !== null ? { endDateString } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.performance(organizationId, startDateString, endDateString),
      queryFn: () => this.httpClient.get<PerformanceMetric[]>('/api/metrics/performance', { params }),
    }).result$
  }

  getActivityMetrics(organizationId: number | null, startDate: Date, endDate: Date, precision: ActivityPrecision) {
    const startDateIso = startDate.toISOString()
    const endDateIso = endDate.toISOString()
    const params = {
      ...(organizationId !== null ? { organizationId: organizationId.toString() } : {}),
      startDate: startDateIso,
      endDate: endDateIso,
      precision,
    }
    return this.query({
      queryKey: queryKeys.metrics.activity(organizationId, startDateIso, endDateIso, precision),
      queryFn: () => this.httpClient.get<ActivityMetricsResult>('/api/metrics/activity', { params }),
    }).result$
  }

  getSupportUserActivity(hours: number, hideInternalOrganizations: boolean) {
    return this.query({
      queryKey: queryKeys.metrics.supportUserActivity(hours, hideInternalOrganizations),
      queryFn: () =>
        this.httpClient.get<SupportUserActivityResult>('/api/metrics/support/user-activity', {
          params: { hours: hours.toString(), hideInternalOrganizations: hideInternalOrganizations.toString() },
        }),
      refetchInterval: 60000,
    }).result$
  }

  getMeetingRoomMetrics(organizationIds: number[]) {
    const params = {
      ...(organizationIds.length > 0 ? { organizationIds } : {}),
    }
    return this.query({
      queryKey: queryKeys.metrics.meetingRoomMetrics(organizationIds),
      queryFn: () => this.httpClient.get<MeetingRoomMetric[]>('/api/metrics/meeting-rooms', { params }),
    }).result$
  }

  exportCompletedCalls(
    userId: number | null,
    organizationIds: number[],
    exclusionFilters: string[],
    viewFilter: string | null,
    timeInterval: TimeInterval,
    nameQuery: string | null,
    sortField: string | null,
    sortDirection: string | null,
    includeAudioMetrics: boolean,
    includeDetailedAudioMetrics: boolean
  ): Observable<void> {
    const fileName = `completed-calls-from-${timeInterval.from?.toISOString()}${timeInterval.to ? `-to-${timeInterval.to.toISOString()}` : ''}.csv`
    const params = {
      ...(userId ? { userId: userId.toString() } : {}),
      ...(organizationIds !== null && organizationIds.length > 0 ? { organizationIds: organizationIds } : {}),
      ...(exclusionFilters.length > 0 ? { exclusionFilters } : {}),
      ...(timeInterval.from ? { fromDateString: timeInterval.from.toISOString() } : {}),
      ...(timeInterval.to ? { toDateString: timeInterval.to.toISOString() } : {}),
      ...(viewFilter ? { viewFilter } : {}),
      ...(nameQuery ? { nameQuery } : {}),
      ...(sortField ? { sortField } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      includeAudioMetrics: includeAudioMetrics ? 'true' : 'false',
      includeDetailedAudioMetrics: includeDetailedAudioMetrics ? 'true' : 'false',
    }
    return this.httpClient
      .get(`/api/metrics/completed-calls/export`, {
        params,
        responseType: 'blob',
        headers: generateHeadersWithTimezone(),
      })
      .pipe(
        tap((data) => {
          this.downloadsService.downloadData(data, fileName, 'csv')
          this.toastService.show('CALLS.EXPORT_COMPLETED')
        }),
        catchError((error) => {
          console.error('Export failed:', error)
          this.toastService.show('CALLS.EXPORT_FAILED')
          return EMPTY
        }),
        map(() => void 0)
      )
  }
}
